import { formatDataStore, formatDataStoreAgentResults } from '/b2b/dataStores/format';
import {
    formatFilterParams,
    formatPagingParams,
    handleError,
    makeResultsFormatter,
} from '/b2b/common/services/helpers';
import {
    formatPrivacyProtocol,
    formatPrivacyProtocolCollection,
    formatUcConfig,
    formatUcConfigConsentDetails,
    formatUcConfigConsentResults,
    formatUcConfigIntegrationOperation,
    formatUcConfigIntegrationResults,
} from './format';

import API from '/b2b/common/helpers/API';
import { API_NAME } from '#config/runtimeconfig';
import AuthenticatedPaths from '/b2b/routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import castArray from 'lodash/castArray';
import { createCollectionPayloadFromState } from './utils';
import { requestOAuthToken } from '/b2b/dataStores/services';
import { connectOAuth2 as sharedConnectOAuth2 } from '/b2b/common/helpers/connectOAuth2';

export const connectOAuth2 = (details, redirect = AuthenticatedPaths.CONNECT_UC_INTEGRATION) => {
    const {
        agentId,
        dataStoreId,
        authType,
        datasource,
        name,
        authorization_url,
        authorizationUrl = authorization_url,
        ...params
    } = details;

    return sharedConnectOAuth2({
        agentId,
        authorizationUrl,
        params,
        redirectPath: redirect,
        // UC historically used no PKCE; keep it opt-out so behavior is unchanged.
        usePKCE: false,
        // Keep UC on its existing token-exchange endpoint (POST /data-mapping/token)
        // so this refactor stays frontend-only and does not change RBAC/entitlements.
        exchangeToken: requestOAuthToken,
        // Preserve UC's return shape: token-exchange paths resolve the raw token
        // payload; the implicit `token` path resolves the assembled body.
        formatResult: ({ body, tokenData }) =>
            tokenData ? tokenData : { agentId, authType, ...body, name },
    });
};

export const fetchPrivacyProtocols = params => {
    const queryStringParameters = {
        ...formatPagingParams(params),
        ...formatFilterParams(params),
        preconfigured: true,
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, `/uc/privacy-protocols`, options)
        .catch(handleError)
        .then(makeResultsFormatter(formatPrivacyProtocol));
};

export const fetchPrivacyProtocolById = id => {
    const options = {};
    return API.get(API_NAME, `/uc/privacy-protocol/${encodeURIComponent(id)}`, options)
        .catch(handleError)
        .then(formatPrivacyProtocol);
};

export const createPrivacyProtocol = state => {
    const options = {
        body: state,
    };
    return API.post(API_NAME, `/uc/privacy-protocol`, options)
        .catch(handleError)
        .then(responseOrId =>
            typeof responseOrId === 'object'
                ? { ...state, ...responseOrId }
                : { privacyProtocolId: responseOrId, ...state }
        )
        .then(formatPrivacyProtocol);
};

export const copyPrivacyProtocolById = id => {
    const options = {
        body: {
            privacyProtocolId: id,
        },
    };
    return API.post(API_NAME, `/uc/privacy-protocol/duplicate`, options)
        .catch(handleError)
        .then(formatPrivacyProtocol);
};

export const updatePrivacyProtocolById = (id, state) => {
    const options = {
        body: state,
    };

    return API.patch(API_NAME, `/uc/privacy-protocol/${encodeURIComponent(id)}`, options)
        .catch(handleError)
        .then(formatPrivacyProtocol);
};

export const deletePrivacyProtocol = idOrIds => {
    const idsToDelete = castArray(idOrIds);
    const options = {
        body: idsToDelete,
        responseType: 'text',
    };
    return API.del(API_NAME, `/uc/privacy-protocols`, options)
        .then(response => {
            try {
                // Check if the response is JSON
                return JSON.parse(response);
            } catch (error) {
                return { status: 'success', code: 200, response };
            }
        })
        .catch(handleError)
        .then(response => {
            const { status, response: resp } = response;
            if (status === 'success') {
                return resp || response || idsToDelete;
            }
            return resp || response;
        });
};

export const fetchPrivacyProtocolCollections = params => {
    const queryStringParameters = {
        ...formatPagingParams(params),
        ...formatFilterParams(params),
        preconfigured: true,
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, `/uc/privacy-protocols`, options)
        .catch(handleError)
        .then(makeResultsFormatter(formatPrivacyProtocolCollection));
};

export const fetchUcConfigs = params => {
    const options = {
        queryStringParameters: {
            ...formatPagingParams(params),
            ...formatFilterParams(params),
        },
    };
    return API.get(API_NAME, `/uc/configs`, options)
        .catch(handleError)
        .then(makeResultsFormatter(formatUcConfig));
};

export const fetchUcConfigById = id => {
    const options = {};
    return API.get(API_NAME, `/uc/config/${encodeURIComponent(id)}`, options)
        .catch(handleError)
        .then(formatUcConfig);
};

export const createUcConfig = state => {
    const body = createCollectionPayloadFromState(state);
    const options = {
        body,
        headers: {
            'Content-Type': body !== state ? 'multipart/form-data' : 'application/json',
        },
    };
    return API.post(API_NAME, `/uc/configs`, options)
        .catch(handleError)
        .then(responseOrId =>
            typeof responseOrId === 'object'
                ? { ...state, ...responseOrId }
                : { id: responseOrId, ...state }
        )
        .then(formatUcConfig);
};

export const copyUcConfigById = (id, overrides) => {
    const body = createCollectionPayloadFromState(overrides);
    const options = {
        body,
        headers: {
            'Content-Type': body !== overrides ? 'multipart/form-data' : 'application/json',
        },
    };
    return API.post(API_NAME, `/uc/config/${encodeURIComponent(id)}/duplicate`, options)
        .catch(handleError)
        .then(formatUcConfig);
};

export const updateUcConfigById = (id, state) => {
    const body = createCollectionPayloadFromState(state);
    const options = {
        body,
        headers: {
            'Content-Type': body !== state ? 'multipart/form-data' : 'application/json',
        },
    };

    return API.patch(API_NAME, `/uc/config/${encodeURIComponent(id)}`, options)
        .catch(handleError)
        .then(formatUcConfig);
};

export const deleteUcConfig = idOrIds => {
    const idsToDelete = castArray(idOrIds);
    const options = {
        body: idsToDelete,
        responseType: 'text',
    };
    return API.del(API_NAME, `/uc/configs`, options)
        .then(response => {
            try {
                // Check if the response is JSON
                return JSON.parse(response);
            } catch (error) {
                return { status: 'success', code: 200, response };
            }
        })
        .catch(handleError)
        .then(response => {
            const { status, response: resp } = response;
            if (status === 'success') {
                return resp || response || idsToDelete;
            }
            return resp || response;
        });
};

export const publishUcConfigById = id => {
    const options = {};
    return API.post(API_NAME, `/uc/config/${encodeURIComponent(id)}`, options).catch(handleError);
};

export const fetchUcConfigConsents = (id, params) => {
    const options = {
        queryStringParameters: {
            ...formatPagingParams(params),
            ...formatFilterParams(params),
        },
    };
    return API.get(API_NAME, `/uc/config/${encodeURIComponent(id)}/consents`, options)
        .catch(handleError)
        .then(formatUcConfigConsentResults);
};

export const fetchUcConfigConsentById = (configId, consentId) => {
    const options = {};

    return API.get(
        API_NAME,
        `/uc/config/${encodeURIComponent(configId)}/consent/${consentId}`,
        options
    )
        .catch(handleError)
        .then(formatUcConfigConsentDetails);
};

export const fetchUcConfigIntegrations = params => {
    const options = {
        queryStringParameters: formatPagingParams(params),
    };
    return API.get(API_NAME, `/uc/integrations`, options)
        .catch(handleError)
        .then(formatUcConfigIntegrationResults);
};

export const fetchUcConfigOperations = (id, params) => {
    const options = {
        queryStringParameters: formatPagingParams(params),
    };
    return API.get(API_NAME, `/uc/integration/${encodeURIComponent(id)}/operations`, options)
        .catch(handleError)
        .then(makeResultsFormatter(formatUcConfigIntegrationOperation));
};

export const fetchUcIntegrationAgents = params => {
    const options = {
        queryStringParameters: formatPagingParams(params),
    };
    return API.get(API_NAME, `/uc/integration-agents`, options)
        .catch(handleError)
        .then(formatDataStoreAgentResults)
        .then(response => {
            const { results } = response;
            return results.reduce((agents, result) => {
                const { agentId, name, schema, ...data } = result;
                agents[agentId] = {
                    ...data,
                    agentId,
                    name,
                    schema,
                };
                return agents;
            }, {});
        });
};

export const createUcConfigIntegration = params => {
    const options = {
        body: params,
    };
    return API.post(API_NAME, `/uc/integrations`, options)
        .catch(handleError)
        .then(responseOrId =>
            typeof responseOrId === 'object'
                ? { ...options.body, ...responseOrId }
                : { id: responseOrId, ...options.body }
        )
        .then(formatDataStore);
};

export const updateUcConfigIntegrationById = (id, state) => {
    const options = {
        body: state,
    };
    return API.patch(API_NAME, `/uc/integration/${id}`, options)
        .catch(handleError)
        .then(responseOrId =>
            typeof responseOrId === 'object'
                ? { ...options.body, ...responseOrId }
                : { id: responseOrId, ...options.body }
        )
        .then(formatDataStore);
};

export const deleteUcConfigIntegration = idOrIds => {
    const idsToDelete = castArray(idOrIds);
    const options = {
        body: idsToDelete,
        responseType: 'text',
    };
    return API.del(API_NAME, `/uc/integrations`, options)
        .then(response => {
            try {
                // Check if the response is JSON
                return JSON.parse(response);
            } catch (error) {
                return { status: 'success', code: 200, response };
            }
        })
        .catch(handleError)
        .then(response => {
            const { status, response: resp } = response;
            if (status === 'success') {
                return resp || response || idsToDelete;
            }
            return resp || response;
        });
};

export const fetchUcConfigOperationData = (id, operation) => {
    const options = {};
    return API.get(
        API_NAME,
        `/uc/integration/${encodeURIComponent(id)}/operation/${operation}`,
        options
    ).catch(handleError);
};

/**
 * @param {UcReportsQueryParams} queryStringParameters
 * @returns {Promise<UcReportsData>}
 */
export const fetchUcReportsDashboard = params => {
    const queryStringParameters = {
        ...formatPagingParams(params),
        ...formatFilterParams(params, true),
    };
    const options = {
        queryStringParameters,
    };
    return API.get(API_NAME, `/uc/reports/dashboard`, options).catch(handleError);
};
